import { PropsWithChildren, ReactNode } from 'react';

import {
  Divider,
  notify,
  PreviewSnippet,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { InlineCode } from 'components/InlineCode';
import { ClmInstanceSelection } from 'features/cluster/autoscaler/components/ClmCompatibleFamiliesPicker';

import { InstanceFamiliesStep } from './InstanceFamiliesStep';

const CLM_STARTUP_TAINTS = `startupTaints:
 - key: live.cast.ai/not-ready 
   value: 
   effect: NoSchedule`;

const Step = ({ number }: { number: number }) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: '50%',
      bgcolor: 'blue.500',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      typography: 'A2',
      flexShrink: 0,
    }}
  >
    {number}
  </Box>
);

type StepRowProps = PropsWithChildren<{
  number: number;
  isLast?: boolean;
  title: ReactNode;
  description: ReactNode;
}>;

const StepRow = ({
  number,
  isLast,
  title,
  description,
  children,
}: StepRowProps) => (
  <Stack direction="row" gap={16}>
    <Stack alignItems="center" width={24} flexShrink={0}>
      <Step number={number} />
      {!isLast && (
        <Divider
          orientation="vertical"
          sx={{ flexGrow: 1, height: 'unset', my: 8 }}
        />
      )}
    </Stack>
    <Box flexGrow={1} pb={isLast ? 0 : 24}>
      <Stack gap={12}>
        <Stack gap={2}>
          <Typography variant="A1">{title}</Typography>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            {description}
          </Typography>
        </Stack>
        {children}
      </Stack>
    </Box>
  </Stack>
);

type Props = {
  clmInstanceSelection?: ClmInstanceSelection;
  onOpenCompatibleInstancesDrawer: () => void;
};

export const SetupSteps = ({
  clmInstanceSelection,
  onOpenCompatibleInstancesDrawer,
}: Props) => (
  <Stack>
    <StepRow
      number={1}
      title="Ensure EC2NodeClass uses AL2023"
      description={
        <>
          Your EC2NodeClass — the one your CLM NodePool references — must use
          Amazon Linux 2023 as the AMI family. To verify, check that{' '}
          <InlineCode>spec.amiFamily</InlineCode> is set to{' '}
          <InlineCode>AL2023</InlineCode> on the referenced NodeClass.
        </>
      }
    />
    <StepRow
      number={2}
      title="Apply the required CLM label"
      description="Add this label to the NodePool template so CLM daemonset gets installed on all nodes."
    >
      <PreviewSnippet
        code='live.cast.ai/install: "true"'
        onCopy={() => notify.success('Copied to clipboard')}
      />
    </StepRow>
    <StepRow
      number={3}
      title="Add startup taint for CLM readiness"
      description="Karpenter waits for CLM's readiness signal instead of spinning up extra nodes."
    >
      <PreviewSnippet
        code={CLM_STARTUP_TAINTS}
        onCopy={() => notify.success('Copied to clipboard')}
      />
    </StepRow>
    <StepRow
      number={4}
      title="Apply CLM-compatible instance types to your NodePool"
      description={
        <>
          Add compatible families to your NodePool&apos;s{' '}
          <InlineCode>spec.requirements</InlineCode>. CRIU checkpoint/restore
          only works between families that share the same CPU architecture.
        </>
      }
    >
      <InstanceFamiliesStep
        clmInstanceSelection={clmInstanceSelection}
        onOpenCompatibleInstancesDrawer={onOpenCompatibleInstancesDrawer}
      />
    </StepRow>
    <StepRow
      number={5}
      title="Pin topology.kubernetes.io/zone to exactly one AZ"
      description={
        <>
          Pin <InlineCode>topology.kubernetes.io/zone</InlineCode> to exactly
          one AZ as IPs can&apos;t be migrated between AZs.
        </>
      }
    />
    <StepRow
      number={6}
      isLast
      title="Replace existing nodes (recommended)"
      description="Perform a rebalancing to replace current nodes with live migration enabled nodes."
    />
  </Stack>
);
